import { useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { DateTime } from "luxon";
import {
  CalendarDate,
  getLocalTimeZone,
  parseDate,
  today,
  type DateValue,
} from "@internationalized/date";
import { Dialog, DialogTrigger, Group } from "react-aria-components";

import { InlineEditableTrigger } from "./inline-editable-trigger";
import { CreatedDateCap, resolveCreatedToast } from "./resolve-created-toast";

import { INoteObservable } from "@/store/note";
import { generateLongDateString } from "@/domains/date/date";
import { toIsoWithOffset } from "@/modules/date-time/locallyCreatedAt";
import { toastModule } from "@/modules/toast";
import { MDSCalendar } from "@/mds/components/date-time/calendars";
import { MDSDateField } from "@/mds/components/date-time/date-field";
import { MDSDateSegment } from "@/mds/components/date-time/date-segment";
import { MDSDateInput } from "@/mds/components/forms";
import { MDSPopover } from "@/mds/components/overlays/popover";
import { MDSIcon, MDSIconKind } from "@/mds/components/icon";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { ZIndex } from "@/domains/design/constants";
import { TrackedEvent, trackEvent } from "@/domains/metrics";

interface Props {
  note: INoteObservable;
  label?: string;
}

const MIN_DATE = parseDate("1970-01-01");

export const clampToEpoch = (value: DateValue): DateValue =>
  value.compare(MIN_DATE) < 0 ? MIN_DATE : value;

export const clampToUpperBound = (value: DateValue, upperBound: DateValue): DateValue =>
  value.compare(upperBound) > 0 ? upperBound : value;

const luxonToCalendarDate = (iso: string): CalendarDate | null => {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return null;
  return new CalendarDate(dt.year, dt.month, dt.day);
};

// The user can edit the creation date up to (but not past) the moment the row
// was actually inserted in Mem. Use the cap's *local* day so the cell-disabled
// boundary lines up with `today(getLocalTimeZone())` semantics.
const computeMaxDate = (quotaCreatedAtIso: string): DateValue => {
  const todayLocal = today(getLocalTimeZone());
  const cap = luxonToCalendarDate(quotaCreatedAtIso);
  if (!cap) return todayLocal;
  return cap.compare(todayLocal) < 0 ? cap : todayLocal;
};

export const EditCreatedDatePopoverTrigger = observer(function EditCreatedDatePopoverTrigger({
  note,
  label: labelOverride,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<DateValue | null>(null);

  const dt = DateTime.fromISO(note.locallyCreatedAt);
  const fullDateLabel = generateLongDateString(dt);
  const label = labelOverride ?? fullDateLabel;
  const persisted = luxonToCalendarDate(note.locallyCreatedAt);
  const maxDate = computeMaxDate(note.quotaCreatedAt);

  // While the popover is open the field and calendar share an in-memory draft
  // so the user can keystroke through segments without committing partial
  // state. We commit (and close) when they pick a calendar cell or press
  // Enter inside the field with a complete, valid value.
  const handleOpenChange = (next: boolean) => {
    if (next) {
      setDraft(persisted);
      trackEvent(TrackedEvent.EditNoteCreatedDateOpen, { note_id: note.id });
    }
    setIsOpen(next);
  };

  const commit = async (value: DateValue | null) => {
    if (!value) return;
    // Snap pre-epoch typed input to the floor and past-cap typed input down to
    // the upper bound. Calendar cells past either bound are already disabled,
    // so these clamps only fire for the typeable MDSDateField path.
    const epochClamped = clampToEpoch(value);
    const clamped = clampToUpperBound(epochClamped, maxDate);
    const lowerCapped = value.compare(epochClamped) !== 0;
    const upperCapped = epochClamped.compare(clamped) !== 0;
    const cap: CreatedDateCap = upperCapped ? "upper" : lowerCapped ? "lower" : "none";
    const previousIso = note.locallyCreatedAt;
    const existing = DateTime.fromISO(previousIso);
    if (!existing.isValid) return;
    const nextDt = existing.set({ year: clamped.year, month: clamped.month, day: clamped.day });
    const nextIso = toIsoWithOffset(nextDt);
    setIsOpen(false);
    trackEvent(TrackedEvent.EditNoteCreatedDateCommit, {
      note_id: note.id,
      previous_locally_created_at: previousIso,
      next_locally_created_at: nextIso,
    });
    await note.setCreatedAt(nextIso);
    toastModule.triggerToast({
      content: resolveCreatedToast({ surface: "date", cap, nextDt }),
    });
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <MDSTooltip config={{ label: "Edit created date", placement: MDSTooltipPlacement.Top }}>
        <InlineEditableTrigger aria-label={`Edit created date — ${fullDateLabel}`}>
          {label}
        </InlineEditableTrigger>
      </MDSTooltip>
      <MDSPopover zIndex={ZIndex.ReactAriaPopover}>
        <Dialog>
          <PopoverContent>
            <MDSDateField
              aria-label="Edit created date"
              value={draft}
              onChange={setDraft}
              minValue={MIN_DATE}
              maxValue={maxDate}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void commit(draft);
                }
              }}
            >
              <FieldGroup>
                <MDSDateInput>{segment => <MDSDateSegment segment={segment} />}</MDSDateInput>
                <FieldIcon kind={MDSIconKind.CalendarDay} />
              </FieldGroup>
            </MDSDateField>
            <MDSCalendar
              ariaLabel="Edit created date"
              value={draft}
              onChange={value => {
                setDraft(value);
                void commit(value);
              }}
              minValue={MIN_DATE}
              maxValue={maxDate}
            />
          </PopoverContent>
        </Dialog>
      </MDSPopover>
    </DialogTrigger>
  );
});

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background.menu.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`;

const FieldGroup = styled(Group)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};

  &[data-focus-within] {
    border-color: ${({ theme }) => theme.colors.border.emphasis};

    /* Sync the trailing calendar icon color with the focused border. */
    & svg {
      color: ${({ theme }) => theme.colors.border.emphasis};
    }
  }
`;

const FieldIcon = styled(MDSIcon)`
  color: ${({ theme }) => theme.colors.icon.secondary};
  margin-left: ${({ theme }) => theme.spacing[2]};
`;
